import type { RichAccessControl } from "@eu.mdotm.api/smart-workspaces";
import {
	useListbox,
	type InnerRefProps,
	FloatingContent,
	ScrollWrapper,
	Banner,
} from "@mdotm/shadcn/legacy/components";
import { TextInput } from "@mdotm/shadcn/mdotm/components";
import { useDebouncedMemo, useSize, propagateRef } from "@mdotm/shadcn/legacy/react-extensions";
import { ForEach } from "@mdotm/utils/react-extra";
import { stableEmptyArray } from "@mdotm/shadcn/legacy/utils";
import { useState, useMemo, useEffect, useCallback, type KeyboardEvent, useRef } from "react";
import { ACLEntryPreview } from "./ACLEntryPreview";

export function UserSearchAutocomplete({
	usersProvider,
	ignoreUserIds = stableEmptyArray,
	onAdd,
}: UserSearchAutocompleteProps): React.JSX.Element {
	const [query, setQuery] = useState("");
	const { value: debouncedQuery } = useDebouncedMemo(() => query, [query], { debounceInterval: 400 });
	const [open, setOpen] = useState(false);

	// The provider filters an in-memory list, so the match runs inline; the debounce above is what
	// keeps it off the keystroke path.
	const filteredUsers = useMemo(
		() => usersProvider(debouncedQuery).filter((user) => ignoreUserIds.includes(user.userId ?? "-") === false),
		[usersProvider, debouncedQuery, ignoreUserIds],
	);

	const [scrollAreaRef, setScrollAreaRef] = useState<HTMLElement | null>(null);
	const handleSelectOutlined = useCallback(
		(i: number) => {
			const liElement = scrollAreaRef?.querySelector(`[data-listbox-index="${i}"]`) as HTMLLIElement;
			if (!liElement) {
				return;
			}
			(liElement.children[0] as HTMLElement | undefined)?.click();
		},
		[scrollAreaRef],
	);

	const [outlinedItemIndex, setOutlinedItemIndex] = useState<number | null>(null);
	const { handleKeyDown: handleKeyDownBase } = useListbox({
		disabled: !open,
		onOutlinedSelected: handleSelectOutlined,
		onOutlinedItemIndexChange: setOutlinedItemIndex,
		outlinedItemIndex,
		interceptCharacters: false,
		filteredOptions: useMemo(() => new Array(filteredUsers.length + 1).fill(0), [filteredUsers.length]),
		onClose: () => setOpen(false),
		pageSize: 3,
	});
	const handleKeyDown = useCallback(
		(e: KeyboardEvent) => {
			if (e.key === "Enter") {
				e.preventDefault();
			}
			handleKeyDownBase(e);
		},
		[handleKeyDownBase],
	);
	const scrollAreaSize = useSize(scrollAreaRef);
	useEffect(() => {
		if (!scrollAreaRef || outlinedItemIndex === null || !scrollAreaSize) {
			return;
		}
		const liElement = scrollAreaRef.querySelector(`[data-listbox-index="${outlinedItemIndex}"]`) as HTMLLIElement;
		if (!liElement) {
			return;
		}
		scrollAreaRef.scrollTop = Math.max(0, liElement.offsetTop - scrollAreaSize.height / 2);
	}, [scrollAreaSize, outlinedItemIndex, scrollAreaRef]);

	const textInputRef = useRef<HTMLInputElement | null>(null);

	const triggerMemo = useCallback(
		({ innerRef }: InnerRefProps<HTMLInputElement>) => {
			const forwardRef = (el: HTMLInputElement | null) => {
				textInputRef.current = el;
				propagateRef(el, innerRef);
			};
			return (
				<TextInput
					placeholder="Search for a member to add"
					innerRef={forwardRef}
					value={query}
					onChangeText={(v) => {
						if (query !== v) {
							setQuery(v);
							setOpen(true);
						}
					}}
					data-qualifier="ShareDialog/SearchAutocomplete"
					onKeyDown={handleKeyDown}
					onFocus={() => setOpen(true)}
				/>
			);
		},
		[handleKeyDown, query],
	);

	return (
		<FloatingContent
			position="bottom"
			focusOnOpen={false}
			open={open && (query.length > 0 || filteredUsers.length > 0)}
			onScrollOutside={() => {
				if (textInputRef.current !== document.activeElement) {
					setOpen(false);
				}
			}}
			onClickAway={() => {
				if (textInputRef.current !== document.activeElement) {
					setOpen(false);
				}
			}}
			trigger={triggerMemo}
		>
			{({ triggerBox }) => (
				<div className="shadow-[0px_8px_32px_rgba(0,0,0,0.16)] rounded-sm bg-white flex flex-col flex-1 min-h-0 py-1">
					<ScrollWrapper
						innerRef={setScrollAreaRef}
						direction="vertical"
						style={{
							width: triggerBox.width,
						}}
						className={{
							"min-h-0 max-h-[300px] sm:max-w-[620px] max-w-[90vw]": true,
						}}
					>
						<div className="py-1">
							{filteredUsers.length === 0 ? (
								query.length > 0 && (
									<Banner severity="error">I cannot find any colleagues with this email address.</Banner>
								)
							) : (
								<>
									<button
										data-listbox-index={0}
										aria-current={outlinedItemIndex === 0}
										type="button"
										className="flex w-full text-left py-2 px-2 hover:bg-[#EDF7F5] aria-current:bg-[#EFF0F3] cursor-pointer"
										onClick={() => {
											onAdd(filteredUsers);
											setQuery("");
											textInputRef.current?.blur();
											setOpen(false);
										}}
										data-qualifier="ShareDialog/UserList/AddAllUser"
									>
										<ACLEntryPreview palette="dashed" className="grow" />
									</button>

									<ForEach collection={filteredUsers}>
										{(item, index) => (
											<button
												data-listbox-index={index + 1}
												aria-current={index + 1 === outlinedItemIndex}
												type="button"
												data-qualifier={`ShareDialog/UserList/User(${item.email})`}
												className="flex w-full text-left py-2 px-2 hover:bg-[#EDF7F5] aria-current:bg-[#EFF0F3] cursor-pointer"
												onClick={() => {
													onAdd([item]);
													setQuery("");
													textInputRef.current?.blur();
													setOpen(false);
												}}
											>
												<ACLEntryPreview palette="dark" className="grow" user={item} />
											</button>
										)}
									</ForEach>
								</>
							)}
						</div>
					</ScrollWrapper>
				</div>
			)}
		</FloatingContent>
	);
}
export type UserSearchAutocompleteProps = {
	usersProvider(query: string): RichAccessControl[];
	ignoreUserIds?: string[];
	onAdd(users: RichAccessControl[]): void;
};
